/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { filterCohorts } from "./cohorts"
import { makeConsumptionRateRange } from "./consumptionRates"
import { Cohort, FinalValuesDistribution, InitialScrsTable } from "./types"
import { assertFiniteColumn } from "./utils"

// AIDEV-NOTE: Bin bounds are read-only once built; naming the shape keeps the
// histogram callbacks readonly without copying the arrays they scan.
type BinRange = { readonly min: number; readonly max: number }

/**
 * Calculate final value proportions from backend intermediate values
 * @param filteredScrs Filtered initial SCRs data with c1, sum_of_ctpt, sum_of_ct
 * @param initialAnnualConsumptionRate The initial annual consumption rate (not monthly)
 * @throws NonFiniteColumnError if any c1, sum_of_ctpt or sum_of_ct value is not a
 *         finite number
 */
export function calculateFinalValueProportions(
  filteredScrs: Pick<InitialScrsTable, "c1" | "sum_of_ctpt" | "sum_of_ct">,
  initialAnnualConsumptionRate: number
): number[] {
  // AIDEV-NOTE: issue #781. All three terms are guarded, not just c1, because the
  // sum below coerces a null in any of them to 0 and returns a plausible wrong
  // proportion. computeFinalValuesDistribution then drops only non-finite results,
  // so a coerced 0 survives into the histogram indistinguishable from real data.
  assertFiniteColumn(filteredScrs.c1, "c1", "calculateFinalValueProportions")
  assertFiniteColumn(filteredScrs.sum_of_ctpt, "sum_of_ctpt", "calculateFinalValueProportions")
  assertFiniteColumn(filteredScrs.sum_of_ct, "sum_of_ct", "calculateFinalValueProportions")

  return filteredScrs.c1.map(
    (c1, i) =>
      c1 +
      filteredScrs.sum_of_ctpt[i] -
      filteredScrs.sum_of_ct[i] * (initialAnnualConsumptionRate / 12)
  )
}

/**
 * Calculate the final values distribution based on initial SCRs data
 * @param initialScrs The initial SCRs data table
 * @param cohort The cohort to filter the data by
 * @param initialAnnualConsumptionRate The initial annual consumption rate (not monthly)
 * @param initialPortfolioDollars The initial portfolio amount in dollars
 * @param binCount Number of bins for the histogram
 */
export function computeFinalValuesDistribution(
  initialScrs: InitialScrsTable,
  cohort: Cohort,
  initialAnnualConsumptionRate: number,
  initialPortfolioDollars: number,
  binCount = 25
): FinalValuesDistribution {
  // Filter data using cohort criteria
  const filteredScrs = filterCohorts(initialScrs, cohort)

  // Calculate final value proportions
  const proportions = calculateFinalValueProportions(filteredScrs, initialAnnualConsumptionRate)

  // Convert to dollar values and filter out invalid values
  const validProportions: number[] = []
  const finalValueDollars: number[] = []

  proportions.forEach(proportion => {
    if (Number.isFinite(proportion)) {
      validProportions.push(proportion)
      finalValueDollars.push(proportion * initialPortfolioDollars)
    }
  })

  if (finalValueDollars.length === 0) {
    return {
      values: [],
      proportions: [],
      statistics: {
        mean: 0,
        median: 0,
        min: 0,
        max: 0,
        proportions: { mean: 0, median: 0, min: 0, max: 0 },
      },
      histogramData: { bins: [], counts: [], rawCounts: [], binRanges: [] },
      annualConsumptionRateUsed: initialAnnualConsumptionRate,
      initialPortfolioDollars,
    }
  }

  // Calculate statistics
  const dollarStats = calculateStatistics(finalValueDollars)
  const proportionStats = calculateStatistics(validProportions)

  // Create histogram
  const histogram = createHistogram(finalValueDollars, binCount)

  return {
    values: finalValueDollars,
    proportions: validProportions,
    statistics: {
      mean: dollarStats.mean,
      median: dollarStats.median,
      min: dollarStats.min,
      max: dollarStats.max,
      proportions: proportionStats,
    },
    histogramData: {
      bins: histogram.bins,
      counts: histogram.counts,
      rawCounts: histogram.rawCounts,
      binRanges: histogram.binRanges,
    },
    annualConsumptionRateUsed: initialAnnualConsumptionRate,
    initialPortfolioDollars,
  }
}

/**
 * Calculate statistics for a set of values
 */
function calculateStatistics(values: readonly number[]) {
  if (values.length === 0) {
    return { mean: 0, median: 0, min: 0, max: 0 }
  }

  const sorted = [...values].sort((a, b) => a - b)

  return {
    mean: values.reduce((sum, v) => sum + v, 0) / values.length,
    median: calculateMedian(sorted),
    min: sorted[0],
    max: sorted[sorted.length - 1],
  }
}

/**
 * Calculate the median of a sorted array of numbers
 */
export function calculateMedian(sortedValues: readonly number[]): number {
  if (sortedValues.length === 0) return 0

  const mid = Math.floor(sortedValues.length / 2)
  return sortedValues.length % 2 === 0
    ? (sortedValues[mid - 1] + sortedValues[mid]) / 2
    : sortedValues[mid]
}

/**
 * Create histogram bins from values with equal bin sizes and boundaries at $0
 * This creates a meaningful visualization where:
 * - All bins have the same width
 * - One bin boundary is exactly at $0 (separating depletion vs remaining funds)
 * - Uses full data range but adjusts bin count based on sample size
 * - Prevents hyper-granularity with small samples
 */
function createHistogram(values: readonly number[], targetBins: number) {
  if (values.length === 0) {
    return { bins: [], counts: [], rawCounts: [], binRanges: [] }
  }

  const sorted = [...values].sort((a, b) => a - b)
  const min = sorted[0]
  const max = sorted[sorted.length - 1]

  // Use actual data range, don't extend to include 0 unless data spans 0
  const effectiveMin = min
  const effectiveMax = max

  const range = effectiveMax - effectiveMin

  // Handle edge case where all values are the same (single data point or identical values)
  if (range === 0) {
    const value = effectiveMin
    const binRanges: BinRange[] = []
    const bins: number[] = []

    // Create bins with 0 as a boundary
    if (value > 0) {
      // For positive values, create bins from 0 to beyond the value
      const binWidth = roundToNiceNumber(value / 5)
      const numBins = Math.ceil(value / binWidth) + 1

      for (let i = 0; i < numBins; i++) {
        const binMin = i * binWidth
        const binMax = (i + 1) * binWidth
        binRanges.push({ min: binMin, max: binMax })
        bins.push((binMin + binMax) / 2)
      }
    } else if (value < 0) {
      // For negative values, create bins from below the value to 0
      const binWidth = roundToNiceNumber(Math.abs(value) / 5)
      const numBins = Math.ceil(Math.abs(value) / binWidth) + 1

      for (let i = -numBins; i < 0; i++) {
        const binMin = i * binWidth
        const binMax = (i + 1) * binWidth
        binRanges.push({ min: binMin, max: binMax })
        bins.push((binMin + binMax) / 2)
      }
    } else {
      // Value is exactly 0: create bins around 0
      const binWidth = 10000 // Default width for zero
      for (let i = -2; i <= 2; i++) {
        const binMin = i * binWidth
        const binMax = (i + 1) * binWidth
        binRanges.push({ min: binMin, max: binMax })
        bins.push((binMin + binMax) / 2)
      }
    }

    // Count values in bins
    const rawCounts = new Array<number>(bins.length).fill(0)
    values.forEach(v => {
      const binIndex = binRanges.findIndex(
        range =>
          v >= range.min && (v < range.max || (v === range.max && range.max === effectiveMax))
      )
      if (binIndex >= 0) {
        rawCounts[binIndex]++
      }
    })

    return {
      bins,
      counts: rawCounts.map(count => (count / values.length) * 100),
      rawCounts,
      binRanges,
    }
  }

  // Calculate appropriate number of bins
  // Use more bins for finer granularity, but only within the actual data range
  const dynamicTargetBins: number = targetBins // Default to the target (usually 25)

  const binRanges: BinRange[] = []
  const bins: number[] = []

  // Check if data spans zero
  const spansZero = effectiveMin < 0 && effectiveMax > 0

  if (spansZero || (effectiveMin >= 0 && effectiveMax > 0)) {
    // For data that includes or is all positive, ensure 0 is a boundary
    const totalRange = effectiveMax - Math.min(effectiveMin, 0)
    const idealBinWidth = totalRange / dynamicTargetBins
    const binWidth = roundToNiceNumber(idealBinWidth)

    // Calculate bin edges as multiples of binWidth
    // For positive-only data, start from 0
    const leftmostBinEdge = effectiveMin < 0 ? Math.floor(effectiveMin / binWidth) * binWidth : 0
    const rightmostBinEdge = Math.ceil(effectiveMax / binWidth) * binWidth

    // Generate all bins from leftmost to rightmost edge
    let currentEdge = leftmostBinEdge
    while (currentEdge < rightmostBinEdge) {
      binRanges.push({
        min: currentEdge,
        max: currentEdge + binWidth,
      })
      bins.push(currentEdge + binWidth / 2)
      currentEdge += binWidth
    }
  } else if (effectiveMin < 0 && effectiveMax <= 0) {
    // For negative-only data, ensure 0 is a boundary
    const totalRange = Math.abs(effectiveMin)
    const idealBinWidth = totalRange / dynamicTargetBins
    const binWidth = roundToNiceNumber(idealBinWidth)

    // Start from the most negative value and go to 0
    const leftmostBinEdge = Math.floor(effectiveMin / binWidth) * binWidth
    const rightmostBinEdge = 0

    // Generate all bins
    let currentEdge = leftmostBinEdge
    while (currentEdge < rightmostBinEdge) {
      binRanges.push({
        min: currentEdge,
        max: currentEdge + binWidth,
      })
      bins.push(currentEdge + binWidth / 2)
      currentEdge += binWidth
    }
  } else {
    // For data that doesn't span zero, use the original simpler approach
    const range = effectiveMax - effectiveMin
    let binWidth = range / dynamicTargetBins

    // Set minimum bin width and round to nice number
    const dataScale = Math.max(Math.abs(effectiveMin), Math.abs(effectiveMax))
    const minBinWidth = getMinBinWidth(dataScale)
    binWidth = Math.max(binWidth, minBinWidth)
    binWidth = roundToNiceNumber(binWidth)

    const numberOfBins = Math.ceil(range / binWidth)

    for (let i = 0; i < numberOfBins; i++) {
      const binMin = effectiveMin + i * binWidth
      const binMax = Math.min(effectiveMin + (i + 1) * binWidth, effectiveMax)
      binRanges.push({ min: binMin, max: binMax })
      bins.push((binMin + binMax) / 2)
    }
  }

  // Initialize bin counts
  const rawCounts = new Array<number>(bins.length).fill(0)

  // Count ALL values in appropriate bins
  values.forEach(value => {
    let binIndex = -1

    // Find which bin this value belongs to
    for (let i = 0; i < binRanges.length; i++) {
      const range = binRanges[i]
      if (
        value >= range.min &&
        (value < range.max || (i === binRanges.length - 1 && value <= range.max))
      ) {
        binIndex = i
        break
      }
    }

    // For values outside our bin range, assign to the nearest edge bin
    if (binIndex === -1) {
      if (value < binRanges[0].min) {
        binIndex = 0 // Assign to first (most negative) bin
      } else if (value > binRanges[binRanges.length - 1].max) {
        binIndex = binRanges.length - 1 // Assign to last (most positive) bin
      }
    }

    if (binIndex >= 0) {
      rawCounts[binIndex]++
    }
  })

  // Convert to probabilities/percentages
  const totalCount = values.length
  const probabilities = rawCounts.map(count => (count / totalCount) * 100)

  return {
    bins,
    counts: probabilities,
    rawCounts,
    binRanges,
  }
}

/**
 * Extract the median annual consumption rate from the consumption rate range
 */
export function getMedianAnnualConsumptionRate(initialScrs: InitialScrsTable): number {
  const annualConsumptionRates = makeConsumptionRateRange(initialScrs)
  return annualConsumptionRates[Math.floor(annualConsumptionRates.length / 2)]
}

/**
 * Get minimum bin width based on data scale
 */
function getMinBinWidth(dataScale: number): number {
  if (dataScale >= 50000000) {
    // >= 50M
    return 1000000 // Min 1M
  } else if (dataScale >= 10000000) {
    // >= 10M
    return 250000 // Min 250K
  } else if (dataScale >= 5000000) {
    // >= 5M
    return 100000 // Min 100K
  } else if (dataScale >= 1000000) {
    // >= 1M
    return 50000 // Min 50K
  } else if (dataScale >= 500000) {
    // >= 500K
    return 25000 // Min 25K
  } else if (dataScale >= 100000) {
    // >= 100K
    return 10000 // Min 10K
  } else {
    return 5000 // Min 5K for smaller values
  }
}

/**
 * Round a number to a nice round value for bin widths
 */
function roundToNiceNumber(value: number): number {
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
  const normalizedValue = value / magnitude
  let roundedValue: number

  if (normalizedValue <= 1) roundedValue = 1
  else if (normalizedValue <= 2) roundedValue = 2
  else if (normalizedValue <= 2.5) roundedValue = 2.5
  else if (normalizedValue <= 5) roundedValue = 5
  else roundedValue = 10

  return roundedValue * magnitude
}
